import { PARSER_CONFIG, REGEX_PATTERNS } from '../constants';
import { TreeNode } from '../types';

import { BaseParser } from './BaseParser';

export class WordDocParser extends BaseParser {
  readonly name = 'WordDocParser';

  canParse(input: string): boolean {
    // Early exit optimizations with specific checks
    if (!this.hasWordDocMarkers(input)) return false;

    const lines = input.split('\n', PARSER_CONFIG.maxLinesToCheck);
    return lines.some((line) => {
      const trimmed = line.trim();
      return (
        trimmed &&
        (REGEX_PATTERNS.numberedList.test(line) || REGEX_PATTERNS.bulletList.test(line) || line.includes('\t'))
      );
    });
  }

  private hasWordDocMarkers(input: string): boolean {
    return this.hasTabIndentation(input) || this.hasBulletMarkers(input) || this.hasNumberedItems(input);
  }

  private hasTabIndentation(input: string): boolean {
    return input.includes('\t');
  }

  private hasBulletMarkers(input: string): boolean {
    // Check for bullet markers at the start of any line (not just the first line)
    return REGEX_PATTERNS.bulletListMultiline.test(input);
  }

  private hasNumberedItems(input: string): boolean {
    return REGEX_PATTERNS.numberedList.test(input);
  }

  protected parseInternal(input: string): TreeNode[] {
    const lines = input.split('\n');
    const indentSize = this.detectIndentSize(lines);
    return this.buildHierarchy(
      lines,
      (line) => this.getIndentLevel(line, indentSize),
      (line) => {
        const trimmed = line.trim();
        return trimmed.replace(REGEX_PATTERNS.numberedListPrefix, '').replace(REGEX_PATTERNS.bulletListPrefix, '');
      }
    );
  }

  private getIndentLevel(line: string, indentSize: number): number {
    const cleanLine = line.replace(REGEX_PATTERNS.carriageReturn, '');
    let indent = 0;
    for (let i = 0; i < cleanLine.length; i++) {
      const char = cleanLine[i];
      if (char === '\t') {
        indent++;
      } else if (char === ' ') {
        let spaces = 0;
        while (i < cleanLine.length && cleanLine[i] === ' ') {
          spaces++;
          i++;
        }
        indent += Math.floor(spaces / indentSize);
        break;
      } else {
        break;
      }
    }
    return indent;
  }
}
